import React, {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";
import { WarningIcon } from "../../icons";
import { classNames } from "../../support/formatters/class-formatter";
import type { Optional } from "../../types";
import styles from "./input.module.css";

export type InputSize = "small" | "medium" | "large";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  onRightIconClick?: Optional<() => void>;
  rightIcon?: Optional<ReactNode>;
  className?: string;
  dataQA?: string;
  dataQaExtra?: string;
  label: string;
  iconTag?: keyof JSX.IntrinsicElements;
  errorMessage?: string;
  size?: InputSize;
}

const RightIcon = ({
  hasError,
  rightIcon,
  iconTag,
  onRightIconClick,
  dataQaExtra,
}: Partial<InputProps & { hasError: boolean }>) => {
  function handleRightIconClick() {
    if (onRightIconClick) {
      onRightIconClick();
    }
  }

  if (hasError) {
    return (
      <WarningIcon
        className={classNames(styles.InputIcon, styles.InputIconError)}
        width="25"
        height="26"
        viewBox="0 0 25 26"
      />
    );
  }
  if (!iconTag || !rightIcon) return null;

  return React.createElement(
    iconTag,
    {
      onClick: handleRightIconClick,
      className: styles.InputIcon,
      ['data-qa']: `${dataQaExtra}`
    },
    rightIcon
  );
};

export const Input = forwardRef(
  (
    {
      onRightIconClick,
      rightIcon,
      className,
      dataQA,
      dataQaExtra,
      label,
      iconTag = "button",
      errorMessage,
      size = "medium",
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const hasError = Boolean(errorMessage);
    const inputContainerClassName = classNames(
      styles.InputContainer,
      className
    );
    const sizeClassMap: Record<InputSize, string> = {
      small: styles.InputFieldSmall,
      medium: styles.InputFieldMedium,
      large: styles.InputFieldLarge,
    };
    const inputClassName = classNames(
      styles.InputField,
      size && sizeClassMap[size],
      hasError && styles.InputFieldError
    );

    const id = useId();
    const inputId = props.id ?? id;

    return (
      <div className={styles.InputWrapper} data-testid="input-wrapper">
        <div
          className={inputContainerClassName}
          data-testid="input-container"
        >
          <input
            className={inputClassName}
            data-testid="input-field"
            placeholder={label}
            ref={ref}
            id={inputId}
            data-qa={dataQA}
            {...props}
          />
          <label
            className={styles.InputLabel}
            data-testid="input-label"
            htmlFor={inputId}
          >
            {label}
          </label>
          <RightIcon {...{ hasError, rightIcon, iconTag, onRightIconClick, dataQaExtra }} />
        </div>
        {hasError && (
          <p className={styles.ErrorMessage} data-testid="error-message">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
